import Big from 'big.js';
import React from 'react';
import styled from 'styled-components';
import { Table, Tooltip } from '@block-explorer/ui';
import {
  FlexRow,
  formatNumber,
  InfoIcon,
  PageTile,
  SvgIcon,
} from '@make-software/cspr-design';

import {
  Avatar,
  CopyButton,
  DetailsAccordionAccountRawData,
  DetailsAccordionAssociatedKeys,
  DetailsCsprAmountTableData,
  DetailsNameTableData,
  DetailsTableRow,
  DetailsValueTableData,
  HistoryLink,
  InfoSvgIcon,
} from '../../../components';
import {
  checkApiHookIsLoading,
  useGetAccountRawData,
  useGetLastBlockInfo,
} from '../../../hooks';
import {
  useCurrencyRateAmount,
  useGetBidderDetailsData,
  useGetDelegatorTotalRewards,
  useGetValidatorsUnstakingBalance,
} from '../../../hooks/event-store-hooks';
import { Trans, useTranslation } from 'react-i18next';
import { VALIDATOR_DETAILS_PATH } from '../../../router/paths';
import {
  useGetValidatorAccountDetails,
  ValidatorType,
} from '../hooks/use-get-validator-account-details-hook';
import { AccountValidatorType } from './AccountValidatorType';
import { buildRouterPath } from '../../../router/utils';
import { deriveAccountInfo, isValidPublicKey } from '../../../utils/account';
import { checkIfValueNotFoundInState } from '../../../domain/error-messages';
import { useSelector } from 'react-redux';
import { RootState } from 'typesafe-actions';
import { AccountInfoStandard } from './AccountInfoStandard';
import { isObjEmpty } from '../../../utils/object';
import useGetAssociatedKeysData from '../../../hooks/use-get-associated-keys-data';
import { AccountInfoCentralized } from './AccountInfoCentralized.tsx';
import { AccountInfoResult } from '@block-explorer/entities';
import { DataResponse } from '../../../services';
import { motesToCSPR } from '../../../utils/currency';

const StyledPageTile = styled(PageTile)(({ theme }) => ({
  padding: '16px 0',
  margin: 0,
}));

export interface AccountDetailsProps {
  publicKey: string;
  accountHash: string;
  data: DataResponse<AccountInfoResult> | null;
  loading: boolean;
  error: string | null;
}

export const AccountDetails = ({
  publicKey,
  accountHash,
  data: accountData,
  loading: accountDataLoading,
  error: accountDataError,
}: AccountDetailsProps) => {
  const { selectedCurrency: currency } = useSelector(
    (state: RootState) => state.currencies
  );

  const { t } = useTranslation();
  const { data: lastBlock } = useGetLastBlockInfo();

  const accountInfo = accountData?.data;
  const isAuctionPoolAccount = accountHash === config.auction_pool_account_hash;
  const undelegatingBalance = accountInfo?.undelegating_balance || '0';
  let validatorType = accountInfo?.auction_status;
  const accountBalance = accountInfo?.balance || '0';
  const genesisBalance = accountInfo?.genesis_balance || '0';
  const formattedGenesisBalance = formatNumber(motesToCSPR(genesisBalance));
  const delegatorStakedBalance = accountInfo?.delegated_balance || '0';

  const eraId = lastBlock?.era_id;
  const auctionEraId = lastBlock && lastBlock?.era_id + 2;

  const {
    data: currencyRate,
    loading: currencyRateLoading,
    error: currencyRateError,
  } = useCurrencyRateAmount({
    currencyCode: currency.id.toString(),
  });

  const {
    data: bidder,
    loading: bidderDataLoading,
    error: bidderError,
  } = useGetBidderDetailsData(
    publicKey && auctionEraId
      ? {
          publicKey: publicKey,
          era_id: auctionEraId,
          includes: 'cspr_name',
        }
      : null
  );

  const {
    data: accountRawData,
    error: accountRawDataError,
    loading: accountRawDataLoading,
  } = useGetAccountRawData({
    accountHash: accountHash,
  });

  const {
    data: delegatorTotalReward,
    loading: delegatorTotalRewardLoading,
    error: delegatorRewardError,
  } = useGetDelegatorTotalRewards({
    publicKey: publicKey,
  });

  const { filteredAssociatedKeys: associatedKeys, actionThresholds } =
    useGetAssociatedKeysData(accountRawData, accountHash);

  const {
    validatorStakedBalance,
    validatorTotalReward,
    validatorTotalRewardLoading,
    validatorTotalRewardError,
  } = useGetValidatorAccountDetails(publicKey, bidder?.data, validatorType);

  validatorType =
    !validatorType && validatorTotalReward
      ? ValidatorType.PAST_VALIDATOR
      : validatorType;
  const {
    data: validatorsUnstakingBalance,
    loading: validatorsUnstakingBalanceLoading,
    error: validatorsUnstakingBalanceError,
  } = useGetValidatorsUnstakingBalance(
    eraId && publicKey
      ? {
          publicKey,
          eraId,
        }
      : null
  );

  const totalBalance = Big(accountBalance)
    .add(delegatorStakedBalance)
    .add(validatorStakedBalance || 0)
    .add(undelegatingBalance)
    .add(validatorsUnstakingBalance?.data || 0)
    .toString();

  const accountEmpty = checkIfValueNotFoundInState(accountRawDataError);

  const balanceDataLoading =
    currencyRateLoading ||
    validatorTotalRewardLoading ||
    delegatorTotalRewardLoading ||
    checkApiHookIsLoading({
      loading: accountRawDataLoading || accountDataLoading,
      error: accountRawDataError || accountDataError,
    });

  const validatorIsStaking =
    validatorType === ValidatorType.ACTIVE_VALIDATOR ||
    validatorType === ValidatorType.PENDING_VALIDATOR;
  const validatorIsBidding =
    validatorType === ValidatorType.ACTIVE_VALIDATOR_BID ||
    validatorType === ValidatorType.INACTIVE_VALIDATOR_BID;

  const hasInfoStandard = !isObjEmpty(accountInfo?.account_info?.info);
  const hasCentralizedInfo = !isObjEmpty(accountInfo?.centralized_account_info);

  const logo = deriveAccountInfo(
    accountInfo?.account_info || accountInfo?.centralized_account_info
  )?.logo;
  const genesisTooltipText = accountInfo?.genesis_balance
    ? `${t('This genesis account was created at the launch of the Casper Network with a balance of ')} ${formattedGenesisBalance} CSPR`
    : null;
  const showUnstakingRow =
    validatorType || validatorsUnstakingBalance?.data > 0;
  return (
    <>
      {hasInfoStandard && (
        <AccountInfoStandard
          publicKey={publicKey}
          accountInfo={accountInfo?.account_info}
          accountInfoLoading={accountDataLoading}
        />
      )}
      {hasCentralizedInfo && (
        <AccountInfoCentralized
          publicKey={accountHash}
          accountInfo={accountInfo}
          accountInfoLoading={accountDataLoading}
        />
      )}
      <StyledPageTile>
        <Table
          paddingBottom={16}
          renderData={() => (
            <>
              <DetailsTableRow>
                <DetailsNameTableData>
                  <Trans t={t}>Public Key</Trans>
                </DetailsNameTableData>
                <DetailsValueTableData
                  monotype
                  loading={!publicKey}
                  colSpan={2}
                >
                  {isValidPublicKey(publicKey) ? (
                    <FlexRow itemsSpacing={8} align={'center'}>
                      <Tooltip
                        limitWidth
                        tooltipContent={t(
                          'This is the readable (hexadecimal) representation of your account’s public key. Use this value as the recipient address for transfers.'
                        )}
                      >
                        {logo ? (
                          <Avatar
                            src={logo}
                            size="small"
                            loading={accountDataLoading}
                            alt={'Account logo'}
                          />
                        ) : (
                          <Avatar
                            hash={publicKey}
                            size="small"
                            loading={accountDataLoading}
                          />
                        )}
                      </Tooltip>
                      <span>{publicKey}</span>
                      <CopyButton value={publicKey} type={'full'} />
                    </FlexRow>
                  ) : (
                    'Unknown'
                  )}
                </DetailsValueTableData>
              </DetailsTableRow>
              <DetailsTableRow>
                <DetailsNameTableData>
                  <Trans t={t}>Account Hash</Trans>
                </DetailsNameTableData>
                <DetailsValueTableData
                  monotype
                  loading={!accountHash}
                  colSpan={2}
                >
                  {accountHash && (
                    <FlexRow itemsSpacing={8} align={'center'}>
                      <Tooltip
                        limitWidth
                        tooltipContent={t(
                          'The account hash is a Blake2B hash of the public key, uniquely identifying your account on the Casper network. DO NOT use this value as the recipient address for CSPR transfers.'
                        )}
                      >
                        <Avatar size="small" hash={accountHash} />
                      </Tooltip>
                      <span>{accountHash}</span>
                      <CopyButton value={accountHash} type={'full'} />
                    </FlexRow>
                  )}
                </DetailsValueTableData>
              </DetailsTableRow>
              {validatorType && (
                <DetailsTableRow>
                  <DetailsNameTableData>
                    <Trans t={t}>Validator status</Trans>
                  </DetailsNameTableData>
                  <DetailsValueTableData loading={!publicKey} colSpan={2}>
                    <FlexRow itemsSpacing={16}>
                      <AccountValidatorType status={validatorType} />
                      <HistoryLink
                        href={buildRouterPath(
                          VALIDATOR_DETAILS_PATH,
                          { id: publicKey },
                          undefined
                        )}
                        ariaDescription={'Link to validator details'}
                      >
                        <Trans t={t}>Validator Profile</Trans>
                      </HistoryLink>
                    </FlexRow>
                  </DetailsValueTableData>
                </DetailsTableRow>
              )}

              <DetailsTableRow>
                <DetailsNameTableData>
                  {validatorType ? (
                    <Trans t={t}>Total Delegator Rewards</Trans>
                  ) : (
                    <Trans t={t}>Total Rewards Received</Trans>
                  )}
                </DetailsNameTableData>
                {isAuctionPoolAccount ? (
                  <DetailsCsprAmountTableData
                    loading={balanceDataLoading && !delegatorTotalReward}
                    errorMessage={null}
                    motes={null}
                    currencyRate={currencyRate?.data?.amount}
                    accountEmpty
                  />
                ) : (
                  <DetailsCsprAmountTableData
                    loading={balanceDataLoading && !delegatorTotalReward}
                    errorMessage={delegatorRewardError}
                    motes={delegatorTotalReward?.data}
                    currencyRate={currencyRate?.data?.amount}
                    accountEmpty={accountEmpty}
                  />
                )}
              </DetailsTableRow>
              {validatorType && (
                <DetailsTableRow>
                  <DetailsNameTableData>
                    <Trans t={t}>Total Validator Rewards</Trans>
                  </DetailsNameTableData>
                  <DetailsCsprAmountTableData
                    loading={balanceDataLoading && !validatorTotalReward}
                    motes={validatorTotalReward}
                    errorMessage={validatorTotalRewardError}
                    currencyRate={currencyRate?.data?.amount}
                    accountEmpty={accountEmpty}
                  />
                </DetailsTableRow>
              )}

              <DetailsTableRow>
                <DetailsNameTableData
                  icon={
                    accountInfo?.genesis_balance ? (
                      <SvgIcon src="assets/icons/ic-genesis-acc.svg" />
                    ) : null
                  }
                  tooltipText={genesisTooltipText}
                >
                  <Trans t={t}>Total Balance</Trans>
                </DetailsNameTableData>
                <DetailsCsprAmountTableData
                  motes={totalBalance}
                  loading={balanceDataLoading && !accountData}
                  errorMessage={null}
                  currencyRate={currencyRate?.data?.amount}
                />
              </DetailsTableRow>
              <DetailsTableRow>
                <DetailsNameTableData indented>
                  <Trans t={t}>Liquid</Trans>
                </DetailsNameTableData>
                <DetailsCsprAmountTableData
                  motes={accountBalance}
                  errorMessage={null}
                  loading={balanceDataLoading && !accountData}
                  currencyRate={currencyRate?.data?.amount}
                />
              </DetailsTableRow>

              {(validatorIsStaking || validatorIsBidding) && (
                <DetailsTableRow>
                  {validatorIsStaking ? (
                    <DetailsNameTableData
                      indented
                      icon={<InfoSvgIcon src={InfoIcon} />}
                      tooltipText={t(
                        'Amount of CSPR the validator account has staked on the network.'
                      )}
                    >
                      <Trans t={t}>Staked as validator</Trans>
                    </DetailsNameTableData>
                  ) : (
                    <DetailsNameTableData indented>
                      <Trans t={t}>Bid as validator</Trans>
                    </DetailsNameTableData>
                  )}

                  <DetailsCsprAmountTableData
                    motes={bidder?.data?.bidAmount}
                    errorMessage={bidderError}
                    loading={
                      (balanceDataLoading && !accountData) || bidderDataLoading
                    }
                    currencyRate={currencyRate?.data?.amount}
                  />
                </DetailsTableRow>
              )}

              <DetailsTableRow>
                <DetailsNameTableData indented>
                  <Trans t={t}>Staked as delegator</Trans>
                </DetailsNameTableData>
                <DetailsCsprAmountTableData
                  motes={delegatorStakedBalance}
                  errorMessage={accountDataError}
                  loading={balanceDataLoading && !accountData}
                  currencyRate={currencyRate?.data?.amount}
                />
              </DetailsTableRow>
              <DetailsTableRow>
                <DetailsNameTableData indented>
                  <Trans t={t}>Undelegating</Trans>
                </DetailsNameTableData>
                <DetailsCsprAmountTableData
                  motes={undelegatingBalance}
                  errorMessage={accountDataError}
                  loading={balanceDataLoading && !undelegatingBalance}
                  currencyRate={currencyRate?.data?.amount}
                />
              </DetailsTableRow>
              {showUnstakingRow ? (
                <DetailsTableRow>
                  <DetailsNameTableData
                    indented
                    icon={<InfoSvgIcon src={InfoIcon} />}
                    tooltipText={t('Bid withdrawn by validator')}
                  >
                    <Trans t={t}>Unstaking</Trans>
                  </DetailsNameTableData>
                  <DetailsCsprAmountTableData
                    motes={validatorsUnstakingBalance?.data}
                    errorMessage={validatorsUnstakingBalanceError}
                    loading={
                      validatorsUnstakingBalanceLoading &&
                      !validatorsUnstakingBalance?.data
                    }
                    currencyRate={currencyRate?.data?.amount}
                  />
                </DetailsTableRow>
              ) : null}
            </>
          )}
        />
        {associatedKeys && associatedKeys.length > 0 && (
          <DetailsAccordionAssociatedKeys
            accordionTitle={<Trans t={t}>Associated keys</Trans>}
            associatedKeys={associatedKeys}
            actionThresholds={actionThresholds}
            loading={accountRawDataLoading}
          />
        )}

        <DetailsAccordionAccountRawData
          accordionTitle={<Trans t={t}>Raw data</Trans>}
          data={accountRawData}
          loading={accountRawDataLoading}
        />
      </StyledPageTile>
    </>
  );
};

export default AccountDetails;
